import {
    IFields,
    IRepository,
} from "@/wc/registry/lib/service/repository/types";
import type { IRepositoryParameters } from "@/wc/registry/lib/service/repository/types";
import { BaseRepositoryPropertiesChecker } from "@/wc/registry/lib/service/repository/base.js";
import { OpenAPIRepositoryManager } from "@/wc/registry/lib/service/repository/openapi/base.js";
import repositoryService from "@/wc/registry/lib/service/repository/index.js";

// Because stekz-components are used to having a single repository for a given
// entity type instead of multiple ones, we use a custom repository to bundle
// all repositories into one.
class BusinessEntitiesManager extends OpenAPIRepositoryManager {
    // In a default repository settings, props are normally used to communicate
    // info that is required to perform the action. In the case of my.stekz,
    // we use the fields to communicate the required info. So basically we're
    // mixing form data and info that the endpoint requires to determine
    // where a certain entity is located.
    async get(props: IRepositoryParameters, fields: IFields | FormData) {
        if ("identity" in props) {
            fields["business_entity_id"] = props.identity;
        }
        return super.get(props, fields);
    }

    // Note, we're now mapping request key 'identity' to 'business_entity_id'
    // as there's no 1-on-1 match between what the endpoint returns as an
    // identifier for the resource and what has to be used to access it.
    async update(props: IRepositoryParameters, fields: IFields) {
        if ("identity" in props) {
            fields["business_entity_id"] = props.identity;
        }
        return super.update(props, fields);
    }

    async delete(props: IRepositoryParameters, fields: IFields) {
        if ("identity" in props) {
            fields["business_entity_id"] = props.identity;
        }
        return super.delete(props, fields);
    }

    async create(props: IRepositoryParameters, fields: IFields) {
        // A required field which we don't show in the table in which the user
        // creates a business entity.
        fields["definition"] = [];
        return super.create(props, fields);
    }
}

export default class BusinessEntityRepository implements IRepository {
    name = "businessEntities";

    constructor() {
        const allActionRepository = repositoryService.get(
            "domainBusinessEntityMgtGetBusinessEntitiesByDomain"
        );
        const createActionRepository = repositoryService.get(
            "domainBusinessEntityMgtCreateBusinessEntity"
        );
        const getActionRepository = repositoryService.get(
            "domainBusinessEntityMgtGetBusinessEntity"
        );
        const updateActionRepository = repositoryService.get(
            "domainBusinessEntityMgtUpdateBusinessEntity"
        );
        const deleteActionRepository = repositoryService.get(
            "domainBusinessEntityMgtDeleteBusinessEntity"
        );
        const actionPaths = {
            "all": allActionRepository.actionPaths.create,
            "create": createActionRepository.actionPaths.create,
            "get": getActionRepository.actionPaths.create,
            "update": updateActionRepository.actionPaths.create,
            "delete": deleteActionRepository.actionPaths.create,
        };

        // The 'get' properties are normally derived from the response schema.
        // Since we actually use a POST endpoint to get the data, we need to
        // map the response schema ourselves.
        actionPaths.get.properties = actionPaths.get.response.properties;

        // Normally the required parameter is automatically parsed from the openapi spec because it is part
        // of the url. E.g. PUT /domains/{domain_id}/business-entities/{identity}, where 'identity' is the parsed
        // part. In our case, we need to add it manually because we use
        // POST /domain/business_entity_mgt/update_business_entity/start.
        actionPaths["get"].requiredParameters.push("identity");
        actionPaths["update"].requiredParameters.push("identity");
        actionPaths["delete"].requiredParameters.push("identity");

        this.actionPaths = actionPaths;
    }

    objects = new BusinessEntitiesManager(this);
    properties = new BaseRepositoryPropertiesChecker(this);
    events = new EventTarget();
    actionPaths = {};
}
